import { render } from 'preact';
import { App, type AppProps } from './App';
import type { WidgetConfig } from './types';
import styles from './styles.css?inline';

export interface InitOptions {
  key: string;
  /** VixSync API origin; defaults to the build-time VITE_API_URL. */
  apiUrl?: string;
  position?: 'left' | 'right';
  /** Open the chat panel right away (used by the dashboard's live preview). */
  startOpen?: boolean;
}

/** The mounted widget, so `preview()` can re-render it with unsaved settings. */
let current: { container: Element; props: AppProps } | null = null;

export const DEFAULT_API_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:4000';

const HOST_ID = 'vixsync-widget';

/** Mounts the widget into its own Shadow DOM. Calling it again replaces the previous instance. */
export function init(options: InitOptions): ShadowRoot {
  if (!options.key) throw new Error('[VixSync] init() requires a widget key');
  destroy();

  const host = document.createElement('div');
  host.id = HOST_ID;
  document.body.appendChild(host);

  const shadow = host.attachShadow({ mode: 'open' });
  const style = document.createElement('style');
  style.textContent = styles;
  shadow.appendChild(style);

  const container = document.createElement('div');
  shadow.appendChild(container);
  const props: AppProps = {
    widgetKey: options.key,
    apiUrl: options.apiUrl ?? DEFAULT_API_URL,
    ...(options.position ? { position: options.position } : {}),
    ...(options.startOpen ? { startOpen: true } : {}),
  };
  current = { container, props };
  render(<App {...props} />, container);
  return shadow;
}

/**
 * Shows unsaved settings (colours, texts, pre-chat form…) on the mounted widget without
 * reloading it. Used by the dashboard's live preview; has no effect before `init()`.
 */
export function preview(settings: Partial<WidgetConfig>): void {
  if (!current) return;
  render(<App {...current.props} override={settings} />, current.container);
}

export function destroy(): void {
  const host = document.getElementById(HOST_ID);
  if (!host) return;
  const container = host.shadowRoot?.lastElementChild;
  if (container) render(null, container);
  host.remove();
  current = null;
}
